import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Users,
  Coins,
  Phone,
  Edit2,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePartners, useAccountsSummary, Partner } from '@/hooks/useAccounts';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import PartnerFormModal from '../components/PartnerFormModal';
import { formatINR } from '@/utils/cashbookCalc';
import type { AccountsContextValue } from '../AccountsLayout';

interface CapitalPartnersTabProps {
  onOpenRecordCapital?: (partnerId?: string) => void;
}

export default function CapitalPartnersTab(props: CapitalPartnersTabProps) {
  const ctx = useOutletContext<AccountsContextValue | undefined>();
  const onOpenRecordCapital = props.onOpenRecordCapital ?? ctx?.openCapitalModal ?? (() => {});
  const { role } = useAuth();
  const isOwnerOrAdmin = role === 'owner' || role === 'admin';

  const { data: partners, isLoading, error, refetch } = usePartners(true);
  const { data: summary } = useAccountsSummary();

  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);

  if (isLoading) return <LoadingState message="Loading partners & capital…" />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} />;

  // Calculate allocated shares
  const totalAllocatedShares = (partners ?? [])
    .filter((p) => p.is_active)
    .reduce((s, p) => s + Number(p.share_percent), 0);

  // Partner summary map (from accounts summary)
  const summaryByPartnerId = new Map(
    (summary?.partners ?? []).map((p) => [p.partner_id, p])
  );

  const handleEditPartner = (p: Partner) => {
    setEditingPartner(p);
    setIsPartnerModalOpen(true);
  };

  const handleAddPartner = () => {
    setEditingPartner(null);
    setIsPartnerModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Top Banner: Equity Share Allocation & Capital Overview */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {/* Total Capital */}
        <div className="obsidian-surface rounded-xl border border-border bg-card p-4 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Total Capital Deposited</span>
            <div className="rounded-lg bg-primary/10 p-1.5 text-primary">
              <Coins className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2 text-xl font-bold text-foreground">
            {formatINR(summary?.capital_total ?? 0)}
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">Lifetime partner equity (tracked separately from bank balance)</p>
        </div>

        {/* Equity Shares Allocated */}
        <div className="obsidian-surface rounded-xl border border-border bg-card p-4 shadow-xs sm:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-medium text-muted-foreground">Equity Share Allocation</span>
              <div className="mt-1 text-sm font-semibold text-foreground">
                {totalAllocatedShares.toFixed(1)}% of 100% Allocated
                {totalAllocatedShares > 100 && (
                  <span className="ml-2 text-xs text-destructive">(Over-allocated!)</span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {isOwnerOrAdmin && (
                <button
                  type="button"
                  onClick={handleAddPartner}
                  className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                >
                  + Add Partner
                </button>
              )}
              <button
                type="button"
                onClick={() => onOpenRecordCapital()}
                className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
              >
                + Deposit Capital
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full transition-all ${
                totalAllocatedShares > 100
                  ? 'bg-destructive'
                  : totalAllocatedShares === 100
                  ? 'bg-emerald-500'
                  : 'bg-primary'
              }`}
              style={{ width: `${Math.min(totalAllocatedShares, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Partners List Table */}
      <div className="obsidian-surface overflow-hidden rounded-xl border border-border bg-card shadow-xs">
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              Partners & Shareholders
            </h3>
            <p className="text-xs text-muted-foreground">
              Equity holders, capital contribution tracking and share percentages
            </p>
          </div>
        </div>

        {partners && partners.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-border bg-muted/40 font-medium text-muted-foreground">
                <tr>
                  <th className="py-2.5 px-3">Partner Name</th>
                  <th className="py-2.5 px-3">Contact</th>
                  <th className="py-2.5 px-3">Equity Share</th>
                  <th className="py-2.5 px-3 text-right">Contributed (₹)</th>
                  <th className="py-2.5 px-3 text-right">% of Capital</th>
                  <th className="py-2.5 px-3 text-center">Deposits</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {partners.map((p) => {
                  const s = summaryByPartnerId.get(p.id);
                  const contributed = s?.contributed ?? 0;
                  const pctOfCap = s?.pct_of_capital ?? 0;
                  const count = s?.entries ?? 0;

                  return (
                    <tr key={p.id} className="hover:bg-muted/30 transition-colors">
                      <td className="py-3 px-3 font-medium text-foreground">
                        <div>
                          <span>{p.name}</span>
                          {p.notes && (
                            <p className="text-[10px] text-muted-foreground line-clamp-1">
                              {p.notes}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">
                        {p.phone ? (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {p.phone}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <span className="font-semibold text-foreground">{p.share_percent}%</span>
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-medium text-emerald-600 dark:text-emerald-400">
                        {formatINR(contributed)}
                      </td>
                      <td className="py-3 px-3 text-right text-muted-foreground font-mono">
                        {pctOfCap}%
                      </td>
                      <td className="py-3 px-3 text-center text-muted-foreground">
                        {count}
                      </td>
                      <td className="py-3 px-3">
                        {p.is_active ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="h-2.5 w-2.5" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                            <XCircle className="h-2.5 w-2.5" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => onOpenRecordCapital(p.id)}
                            className="rounded px-2 py-1 text-[11px] text-primary hover:bg-primary/10"
                            title="Deposit capital for this partner"
                          >
                            + Deposit
                          </button>
                          {isOwnerOrAdmin && (
                            <button
                              type="button"
                              onClick={() => handleEditPartner(p)}
                              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                              title="Edit partner"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center text-xs text-muted-foreground">
            No partners defined yet. Click "+ Add Partner" to create equity holders.
          </div>
        )}
      </div>

      {/* Partner Edit/Create Modal */}
      <PartnerFormModal
        isOpen={isPartnerModalOpen}
        onClose={() => setIsPartnerModalOpen(false)}
        partner={editingPartner}
      />
    </div>
  );
}
